import React, { useState, useEffect } from 'react';
import AskAlex from '../topics/AskAlex';
import { api } from '../../shared/utils/api';
import { formatExplanation } from '../../shared/utils/formatExplanation';
import { cleanMisconception } from '../../shared/utils/misconception';

export default function QuestionReviewModal({ questionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api(`/api/questions/${questionId}/review`)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [questionId]);

  if (loading) return (
    <div className="qp-review-overlay" onClick={onClose}>
      <div className="qp-review-modal" onClick={e => e.stopPropagation()}>
        <div className="qp-empty">Loading</div>
      </div>
    </div>
  );

  if (!data || !data.question) return (
    <div className="qp-review-overlay" onClick={onClose}>
      <div className="qp-review-modal" onClick={e => e.stopPropagation()}>
        <div className="qp-empty">Could not load question.</div>
        <button className="btn-secondary" onClick={onClose}>Close</button>
      </div>
    </div>
  );

  const { question, attempt } = data;
  const options = question.options || [];
  const correct_answer = question.correct_answer;
  const selected_answer = attempt?.selected_answer;
  const isCorrect = attempt?.correct;

  const selectedMisconception = (() => {
    if (isCorrect) return null;
    const dm = question.distractor_misconceptions;
    if (!dm || typeof dm !== 'object') return null;
    return dm[selected_answer] || null;
  })();

  const alexInitialMessage = !isCorrect && selected_answer
    ? `I got this question wrong: "${question.stem}". I chose ${selected_answer} but the correct answer was ${correct_answer}. Can you explain why?`
    : null;

  return (
    <div className="qp-review-overlay" onClick={onClose}>
      <div className="qp-review-modal" onClick={e => e.stopPropagation()}>
        <div className="qp-review-header">
          <span className="qp-review-title">Question review</span>
          <button className="qp-review-close" onClick={onClose}>×</button>
        </div>

        {question.scenario && (
          <div className="qp-scenario">{question.scenario}</div>
        )}
        <div className="qp-stem">{question.stem}</div>

        <div className="qp-options">
          {(() => {
            const correctU = (correct_answer || '').toString().toUpperCase();
            const selectedU = (selected_answer || '').toString().toUpperCase();
            return options.map(opt => {
              const letter = opt.letter;
              const letterU = (letter || '').toString().toUpperCase();
              const isCorrectOpt = letterU === correctU;
              const isWrongOpt = letterU === selectedU && !isCorrect;
              let cls = 'qp-option';
              if (isCorrectOpt) cls += ' qp-option-correct';
              else if (isWrongOpt) cls += ' qp-option-incorrect';
              return (
                <button key={letter} className={cls} disabled>
                  <span className="qp-option-letter">{letter}</span>
                  <span className="qp-option-text">{opt.text}</span>
                </button>
              );
            });
          })()}
        </div>

        <div className="qp-review-result-row">
          <span className={`qp-review-verdict ${isCorrect ? 'qp-review-verdict-correct' : 'qp-review-verdict-incorrect'}`}>
            {isCorrect ? 'Correct' : 'Incorrect'}
          </span>
        </div>

        {selectedMisconception && (
          <div className="qp-misconception-wrap">
            <div className="qp-misconception-header">
              <span className="qp-misconception-label">Common misconception</span>
            </div>
            <div className="qp-misconception-body">{cleanMisconception(selectedMisconception)}</div>
          </div>
        )}

        {question.explanation && (
          <div className="qp-explanation">
            <div className="qp-explanation-label">Explanation</div>
            <div className="qp-explanation-text">
              {formatExplanation(question.explanation).map(p => (
                <p key={p.key} className="qp-explanation-para" dangerouslySetInnerHTML={{ __html: p.__html }} />
              ))}
            </div>
          </div>
        )}

        <div className="qp-review-breadcrumb">
          <span>{question.subject_area}</span>
          <span>›</span>
          <span>{question.subtopic}</span>
        </div>

        {question.id && !isCorrect && (
          <AskAlex
            questionId={question.id}
            wasCorrect={false}
            initialMessage={alexInitialMessage}
          />
        )}

        <div className="qp-review-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
